import { useCallback, useEffect, useRef, useState } from "react";

import { LogoutReason } from "@/constants/Login";
import useAuth from "@/hooks/useAuth";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useLoginInitialization from "@/hooks/useLoginInitialization";
import {
  useAppStartupDispatch,
  useIsRootNavigatorReadySelector,
} from "@/store/reducers/appStartup";
import { useLogoutReasonSelector } from "@/store/reducers/auth";
import ErrorBoundaryUI from "@/ui/components/errorBoundary/ErrorBoundaryUI";
import EquipSplashScreen from "@/ui/components/splashScreen/EquipSplashScreen";

const usePreLoginCleanup = () => {
  const logoutReason = useLogoutReasonSelector();
  const isRootNavigatorReady = useIsRootNavigatorReadySelector();
  const { setCanLoggedInUserSeeAppContent } = useAppStartupDispatch();
  const {
    clearWebSession,
    isAuthenticated,
    isLoading: isAuth0Loading,
  } = useAuth();
  const [isPreLoginCleanupDone, setIsPreLoginCleanupDone] =
    useState<boolean>(false);

  const onLogoutFinally = useCallback(
    async (reason: LogoutReason) => {
      // do not hard redirect the user to Auth0 /v2/logout if they're just logging in
      // and this hook is being executed just to clean up stale state pre-login
      if (reason === LogoutReason.JustPreLoginCleanup) {
        // otherwise, if user is actually logging out:
        //  - not marking preclean as "done" blocks authorize() below from executing
        //    - since authorize() does its own redirect to Auth0's Universal Login page
        //    - and this would cause an unnecessary race condition with the intended /v2/logout redirect
        setIsPreLoginCleanupDone(true);
        return;
      }

      // if here because v2 login is disabled, the clearWebSession's returnToUrl is set to my-equip-v1 to route there

      // this redirects to Auth0 /v2/logout, so soon after this executes, no further code is executed
      // hence why this must be in the finally block, as the last thing to run
      await clearWebSession(reason);
    },
    [clearWebSession],
  );

  // on mobile, this runs immediately/automatically on mount
  // on web, due to the very different login/logout flows, this runs only once web is ready
  const [canLoginInitializationBegin, setCanLoginInitializationBegin] =
    useState<boolean>(false);

  const { isRestartRequired } = useLoginInitialization({
    canLoginInitializationBegin,
    onLogoutFinally,
  });

  const handledLogoutAlready = useRef<boolean>(false);
  useEffect(() => {
    if (handledLogoutAlready.current) return;

    // once ready, trigger loginInitialization to logout/prelogin-cleanup
    if (isAuth0Loading) return;
    if (!isRootNavigatorReady) return;

    if (logoutReason === LogoutReason.JustPreLoginCleanup && isAuthenticated) {
      // 1. user is not logging out                     -> skip this cleanup
      // 2. user is already logged in, just a new tab   -> skip the login page entirely
      handledLogoutAlready.current = true;
      setCanLoggedInUserSeeAppContent(false);
      return;
    }

    handledLogoutAlready.current = true;
    setCanLoginInitializationBegin(true);
  }, [
    isAuth0Loading,
    isAuthenticated,
    isRootNavigatorReady,
    logoutReason,
    setCanLoggedInUserSeeAppContent,
  ]);

  return { isPreLoginCleanupDone, isRestartRequired };
};

const LoginWeb = () => {
  const { isMyEquipV2LoginEnabled } = useFeatureFlags();
  const isRootNavigatorReady = useIsRootNavigatorReadySelector();
  const { authorize, isLoading: isAuth0Loading } = useAuth();
  const { isPreLoginCleanupDone, isRestartRequired } = usePreLoginCleanup();

  useEffect(() => {
    // once ready and loginInitialization done, trigger login
    if (isAuth0Loading) return;
    if (!isRootNavigatorReady) return;
    // if FF off, RootNavigator's webAuthFlow will immediately re-route to my-equip-v1
    if (!isMyEquipV2LoginEnabled) return;
    if (!isPreLoginCleanupDone) return;

    // this redirects to auth0.com/login, so soon after this executes, no further code is executed
    // hence why this must be the last thing to run here
    void authorize();
  }, [
    authorize,
    isAuth0Loading,
    isPreLoginCleanupDone,
    isMyEquipV2LoginEnabled,
    isRootNavigatorReady,
  ]);

  if (isRestartRequired) {
    return <ErrorBoundaryUI />;
  }
  return <EquipSplashScreen />;
};

export default LoginWeb;
